// documentPaymentsModel.js
//
// Field reads + paid rollups for DOCUMENT_PAYMENT (Invoice / Proposal AR).

import { parsePriceCents } from 'utilities/Currency';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getEnumOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { formatPurchaseOrderDate } from 'nodetypes/purchaseorder/view/purchaseOrderGeneralInfoModel';
import {
  DOCUMENT_PAYMENT_TYPE_KEY,
  isDocumentPaymentTypeKey,
} from 'nodetypes/documents/payments/documentPaymentKeys';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function toDocumentPaymentMoneyNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const cents = parsePriceCents(value);
  return Number.isFinite(cents) ? cents : null;
}

/**
 * @param {object|null|undefined} payment
 * @param {string} fieldName
 * @returns {unknown}
 */
export function readDocumentPaymentField(payment, fieldName) {
  return readNodeFieldRawValue(
    payment,
    { name: fieldName, source: 'document_payment' },
    DOCUMENT_PAYMENT_TYPE_KEY,
  );
}

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isDocumentPaymentPaidStatus(status) {
  return normalizeString(status).toLowerCase() === 'paid';
}

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isDocumentPaymentOpenStatus(status) {
  const normalized = normalizeString(status).toLowerCase();
  return normalized === 'scheduled' || normalized === 'due';
}

/**
 * @param {object[]|null|undefined} neighborNodes
 * @returns {object[]}
 */
export function filterDocumentPayments(neighborNodes) {
  return (Array.isArray(neighborNodes) ? neighborNodes : [])
    .filter((row) => isDocumentPaymentTypeKey(row?.type))
    .sort((left, right) => {
      const leftOrder = Number(readDocumentPaymentField(left, 'sortOrder'));
      const rightOrder = Number(readDocumentPaymentField(right, 'sortOrder'));
      if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return normalizeString(left?.title).localeCompare(normalizeString(right?.title));
    });
}

/**
 * Sum of amounts for payments marked Paid (excludes void/canceled).
 *
 * @param {object[]} payments
 * @returns {number}
 */
export function sumPaidDocumentPayments(payments) {
  return (Array.isArray(payments) ? payments : []).reduce((sum, payment) => {
    const status = readDocumentPaymentField(payment, 'status');
    if (!isDocumentPaymentPaidStatus(status)) return sum;
    const amount = toDocumentPaymentMoneyNumber(readDocumentPaymentField(payment, 'amount'));
    return sum + (amount == null ? 0 : amount);
  }, 0);
}

/**
 * Derive invoice/proposal documentStatus from payment rollups.
 * Does not overwrite void. Leaves draft/sent/approved when nothing is paid.
 *
 * @param {object} options
 * @param {unknown} [options.currentStatus]
 * @param {number} options.total
 * @param {number} options.amountPaid
 * @param {number} options.balanceDue
 * @returns {string|null} Next status, or null when no change suggested
 */
export function deriveDocumentArStatus({
  currentStatus,
  total,
  amountPaid,
  balanceDue,
}) {
  const current = normalizeString(currentStatus).toLowerCase();
  if (current === 'void') return null;

  const safeTotal = Number(total) || 0;
  const safePaid = Number(amountPaid) || 0;
  const safeBalance = Number(balanceDue);

  if (safePaid <= 0) return null;

  if (safeTotal > 0 && safeBalance <= 0) {
    return current === 'paid' ? null : 'paid';
  }

  if (safeBalance > 0) {
    return current === 'partially_paid' ? null : 'partially_paid';
  }

  return null;
}

/**
 * @param {object|null|undefined} payment
 * @param {object|null|undefined} [paymentType]
 * @param {string} [language='en']
 * @returns {object}
 */
export function getDocumentPaymentRowModel(payment, paymentType = null, language = 'en') {
  const fields = Array.isArray(paymentType?.schema?.fields) ? paymentType.schema.fields : [];
  const termsField = fields.find((field) => normalizeString(field?.name) === 'paymentTerms') || null;
  const statusField = fields.find((field) => normalizeString(field?.name) === 'status') || null;
  const methodField = fields.find((field) => normalizeString(field?.name) === 'method') || null;

  const paymentTerms = normalizeString(readDocumentPaymentField(payment, 'paymentTerms'));
  const status = normalizeString(readDocumentPaymentField(payment, 'status'));
  const method = normalizeString(readDocumentPaymentField(payment, 'method'));
  const dueDate = readDocumentPaymentField(payment, 'dueDate');
  const amountBasis = normalizeString(readDocumentPaymentField(payment, 'amountBasis')) || 'fixed';
  const percentOfTotal = readDocumentPaymentField(payment, 'percentOfTotal');

  const termsOptions = termsField ? resolveEnumOptionsForField(termsField, language) : [];
  const statusOptions = statusField ? resolveEnumOptionsForField(statusField, language) : [];
  const methodOptions = methodField ? resolveEnumOptionsForField(methodField, language) : [];

  const percentNumber = percentOfTotal === null || percentOfTotal === undefined || percentOfTotal === ''
    ? null
    : Number(percentOfTotal);

  return {
    key: normalizeString(payment?.key),
    title: normalizeString(payment?.title) || 'Untitled payment',
    amount: toDocumentPaymentMoneyNumber(readDocumentPaymentField(payment, 'amount')),
    amountBasis,
    percentOfTotal: Number.isFinite(percentNumber) ? percentNumber : null,
    paymentTerms,
    paymentTermsLabel: paymentTerms
      ? (getEnumOptionLabel(termsOptions, paymentTerms, language) || paymentTerms)
      : '',
    dueDate,
    dueDateLabel: formatPurchaseOrderDate(dueDate),
    status,
    statusLabel: status
      ? (getEnumOptionLabel(statusOptions, status, language) || status)
      : '',
    method,
    methodLabel: method
      ? (getEnumOptionLabel(methodOptions, method, language) || method)
      : '',
    reference: normalizeString(readDocumentPaymentField(payment, 'reference')),
    paidDate: readDocumentPaymentField(payment, 'paidDate'),
    source: normalizeString(readDocumentPaymentField(payment, 'source')),
    sortOrder: Number(readDocumentPaymentField(payment, 'sortOrder')),
  };
}
